import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import {
	Exposure,
	ExposureCreateRequest,
	Peril,
	PerilCreateRequest,
} from '../../../types/policy';
import { tableStyles } from '../../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../../util/tableUtil';
import { StyledEmptyMessage, StyledTableActions } from '../../common';
import SortableTable from '../../SortableTable';
import { getPerilDisplayNameAndUrl } from './util';

interface Props {
	perils: Peril[] | PerilCreateRequest[];
	authData: AuthData;
	endorsementData: EndorsementData;
	openConfirmationModal: (peril: Peril | PerilCreateRequest) => void;
}

interface Data {
	perilId: string;
	perilName: string;
	dateCreated: string;
	actions: any;
	link: string;
	isActive: boolean;
}

function createData(
	perilId: string,
	perilName: string,
	dateCreated: string,
	actions: any,
	link: string,
	isActive: boolean,
): Data {
	return { perilId, perilName, dateCreated, actions, link, isActive };
}

/**
 * The table element for AddedExposurePerils and ExistingExposurePerils. The 'perils' prop should be
 * controlled by the parent element and should be be added to in this component. This component is only
 * used to transform a list of Peril | PerilCreateRequest objects to a list of rows to display in the
 * table.
 */
const PerilsList = (props: Props) => {
	const { t } = useTranslation();
	const { perils, openConfirmationModal, endorsementData, authData } = props;
	const { endorsementUpdateObj, selectedExposure, productConfig } =
		endorsementData;

	const classes = tableStyles();
	const isInEditMode =
		authData.hasPermission('endorsement.update') &&
		endorsementUpdateObj.state === 'application';

	const exposureName =
		(selectedExposure as Exposure).name ||
		(selectedExposure as ExposureCreateRequest).exposureName;
	const exposureConfig = productConfig.policyConfiguration.exposures.find(
		(e) => e.name === exposureName,
	);

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('dateCreated');

	const emptyTable = !perils.length ? (
		<StyledEmptyMessage>No {t('peril.plural.upper')}</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{
			id: 'perilName',
			rightAlign: false,
			label: `${t('peril.singular.upper')} Name`,
		},
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
		{ id: 'isActive', rightAlign: false, label: 'Status' },
	];

	if (isInEditMode) {
		headCells.push({ id: 'actions', rightAlign: false, label: 'Actions' });
	}

	const rows: any = (perils as Peril[])
		.filter((peril: Peril | PerilCreateRequest) => {
			const { isActive } = getPerilDisplayNameAndUrl(
				selectedExposure,
				peril,
				endorsementUpdateObj,
				exposureConfig,
				endorsementData.policy,
			);
			return isActive;
		})
		.map((peril: Peril | PerilCreateRequest) => {
			const {
				urlId: perilUrlId,
				perilDisplayName,
				isActive,
			} = getPerilDisplayNameAndUrl(
				selectedExposure,
				peril,
				endorsementUpdateObj,
				exposureConfig,
				endorsementData.policy,
			);
			const displayId =
				(peril as Peril).displayId || (peril as PerilCreateRequest).locator;
			const createdTimestamp = (peril as Peril).createdTimestamp
				? (peril as Peril).createdTimestamp
				: `${Date.now()}`;

			const removeOnClick = (e: any) => {
				e.preventDefault();
				e.stopPropagation();
				openConfirmationModal(peril);
			};

			const actions = (
				<StyledTableActions>
					{isInEditMode && <a onClick={removeOnClick}>Remove</a>}
				</StyledTableActions>
			);
			return createData(
				displayId,
				perilDisplayName,
				createdTimestamp,
				actions,
				`${perilUrlId}`,
				isActive,
			);
		});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={row.perilId}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell>{row.perilName}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					<TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
					{row.isActive && isInEditMode && <TableCell>{row.actions}</TableCell>}
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withEndorsementContext<Props>(PerilsList);
